import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shop } from '@/types/shop';
import { RootState } from '@/store';
import { useSelector } from 'react-redux';
import VendorSelectionMapPanel from './VendorSelectionMapPanel';
import { ShopItem } from '../reservations/detail';
import { NAVER_MAP_CLIENT_ID } from '@/components/common/NaverMap/config/keys';
import { GA_EVENT, trackEvent } from '@/utils/gaUtil';


function hasCoords(shop: Shop) {
  return typeof (shop as any).lat === 'number' && typeof (shop as any).lng === 'number';
}

export default function VendorSelection() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedShops, setSelectedShops] = useState<Shop[]>([]);
  const [fixedVendorIds, setFixedVendorIds] = useState<Set<number>>(new Set());
  const [tourSize, setTourSize] = useState(3);
  const [mobileMapOpen, setMobileMapOpen] = useState(false);
  const [desktopMapZoom, setDesktopMapZoom] = useState(14);
  const [selectedMarkerId, setSelectedMarkerId] = useState<number | null>(null);

  const { shops } = useSelector((state: RootState) => state.shop);
  const filteredVendors = useMemo(() => {
    return shops.filter((shop) =>
      shop.shopName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [shops, searchQuery]);
  
  const shopMap = useMemo(() => {
    return (shops || [])
      .map((v) => ({ ...v, lng: v.lon }))
      .reduce((acc: Record<number, ShopItem>, shop: ShopItem) => {
        acc[Number(shop.id)] = shop;
        return acc;
      }, {});
  }, [shops]);
  const selectedShopsWithCoords = useMemo(
    () => selectedShops.map(v => ({ ...v, lng: v.lon })),
    [selectedShops]
  );
  const toggleVendor = (shop: Shop) => {
    const exists = selectedShops.find((v) => v.id === shop.id);

    if (exists) {
      setSelectedShops((prev) => prev.filter((v) => v.id !== shop.id));
      setFixedVendorIds((prev) => {
        const next = new Set(prev);
        next.delete(shop.id);
        return next;
      });
      setSelectedMarkerId((prev) => (prev === shop.id ? null : prev));
      return;
    }

    setSelectedShops((prev) => [...prev, shop]);
  };

  const toggleFixed = (shopId: number) => {
    setFixedVendorIds((prev) => {
      const next = new Set(prev);
      if (next.has(shopId)) next.delete(shopId);
      else next.add(shopId);
      return next;
    });
  };

  const removeVendor = (shopId: number) => {
    setSelectedShops((prev) => prev.filter((v) => v.id !== shopId));
    setFixedVendorIds((prev) => {
      const next = new Set(prev);
      next.delete(shopId);
      return next;
    });
    setSelectedMarkerId((prev) => (prev === shopId ? null : prev));
  };

  const handleContinue = () => {
    const selectedShopIds = selectedShops.map((shop) => shop.id);
    const fixedShopIds = Array.from(fixedVendorIds);
    try {
      trackEvent(GA_EVENT.COMBINATION_SEARCH, {
        booking_path: "COMBINATION",
        tour_size: tourSize,
        selected_shop_count: selectedShopIds.length,
        fixed_shop_count: fixedShopIds.length,
        selected_shop_ids: selectedShopIds.join(","),
        fixed_shop_ids: fixedShopIds.join(","),
      });
    } catch (e) {
      
    }

    navigate(
      `/monthly-availability?tourSize=${tourSize}&selectedShopIds=${selectedShops
        .map((shop) => shop.id)
        .join(',')}&fixedShopIds=${Array.from(fixedVendorIds).join(',')}`
    );
  };

  const decrementTourCount = () => {
    if (tourSize > 2) setTourSize((prev) => prev - 1);
  };

  const incrementTourCount = () => {
    if (tourSize < 3) setTourSize((prev) => prev + 1);
  };

  const getStatusMessage = () => {
    const selectedCount = selectedShops.length;
    const fixedCount = fixedVendorIds.size;

    if (selectedCount < tourSize) {
      const needed = tourSize - selectedCount;
      return `선택한 업체 ${selectedCount}개에 추천 업체 ${needed}개를 추가해 일정을 찾습니다`;
    }

    if (selectedCount === tourSize) {
      return `선택한 ${tourSize}개 업체로 가능한 일정을 찾습니다`;
    }

    if (fixedCount === 0) {
      return (
        <>
          <div>{`선택한 ${selectedCount}개 업체 중 가능한 ${tourSize}개 조합을 찾습니다`}</div>
          <div className="text-blue-600 mt-1">업체명을 클릭하면 고정할 수 있습니다</div>
        </>
      );
    }

    if (fixedCount === 1) {
      const remaining = tourSize - fixedCount;
      return `고정된 업체 ${fixedCount}개를 포함해 선택된 업체 중 ${remaining}개를 찾아 일정을 구성합니다`;
    }

    const remaining = tourSize - fixedCount;
    return `고정된 업체 ${fixedCount}개를 포함해 선택된 업체 중 ${remaining}개를 추가합니다`;
  };

  const canContinue = selectedShops.length >= 2;

  return (
    <div className="min-h-screen bg-gray-50 md:h-screen md:overflow-hidden">
      
      <div className="md:grid md:grid-cols-[minmax(420px,1fr)_minmax(420px,0.95fr)] md:h-screen">
        {/* LEFT: mobile list UI reused */}
        
        <div className="flex flex-col min-h-screen md:min-h-0 md:border-r md:border-gray-200 bg-white">
          <div className="bg-white px-5 py-4 shadow-sm border-b border-gray-100">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl text-gray-700"></i>
              </button>

              <h2 className="text-md font-bold text-gray-900">
                드레스 투어 일정 찾기
              </h2>

              <div className="flex items-center gap-1">
                {/* <button
                  onClick={handlePrevMonth}
                  className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-xl text-gray-700"></i>
                </button> */}
                {/* <button
                  onClick={handleNextMonth}
                  className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-xl text-gray-700"></i>
                </button> */}
              </div>
            </div>
          </div>
          <div className="bg-white px-5  pb-2 shadow-sm md:shadow-none md:border-b md:border-gray-100">
            <div className="mt-2 flex items-center justify-between px-2 md:px-0">
              <p className="text-sm font-medium text-gray-700">투어 업체 수</p>
              <div className="flex items-center gap-3 bg-gray-100 rounded-2xl px-4 py-2">
                <button
                  onClick={decrementTourCount}
                  disabled={tourSize <= 2}
                  className={`w-7 h-7 flex items-center justify-center rounded-full transition-all ${
                    tourSize <= 2
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm'
                  }`}
                >
                  <i className="ri-subtract-line text-base"></i>
                </button>
                <span className="text-xl font-bold text-gray-900 w-8 text-center">
                  {tourSize}
                </span>
                <button
                  onClick={incrementTourCount}
                  disabled={tourSize >= 3}
                  className={`w-7 h-7 flex items-center justify-center rounded-full transition-all ${
                    tourSize >= 3
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 hover:bg-gray-50 shadow-sm'
                  }`}
                >
                  <i className="ri-add-line text-base"></i>
                </button>
              </div>
            </div>
          </div>

          <div className="px-5 py-2 bg-white border-b border-gray-100">
            <div className="relative">
              <input
                type="text"
                placeholder="업체명을 입력하세요"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pr-10 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <i className="ri-search-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto pb-1 md:pb-6">
            <div className="bg-white">
              {filteredVendors.map((shop, index) => {
                const isSelected = selectedShops.some((v) => v.id === shop.id);
                const isFixed = fixedVendorIds.has(shop.id);

                return (
                  <div key={shop.id}>
                    <button
                      onClick={() => toggleVendor(shop)}
                      className="w-full px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className={`text-sm font-medium truncate ${
                            isSelected ? 'text-blue-600' : 'text-gray-900'
                          }`}
                        >
                          {shop.shopName}
                        </span>
                        {isFixed && (
                          <span className="flex-shrink-0 text-xs bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded-full font-medium">
                            고정
                          </span>
                        )}
                      </div>

                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <i className="ri-check-line text-white text-sm"></i>
                        )}
                      </div>
                    </button>

                    {index < filteredVendors.length - 1 && (
                      <div className="mx-5 border-b border-gray-100"></div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className='md:hidden' style={{height: 360}}></div>
          </div>
        </div>

        {/* RIGHT: map + desktop CTA */}
        <div className="hidden md:flex flex-col min-h-0 bg-white relative">
          <div className="flex-1 min-h-0">
            <VendorSelectionMapPanel
              clientId={NAVER_MAP_CLIENT_ID}
              selectedShops={selectedShopsWithCoords}
              fixedVendorIds={fixedVendorIds}
              selectedMarkerId={selectedMarkerId}
              onSelectMarker={setSelectedMarkerId}
              mapMarkerPositionBottom={200}
              zoomLevel={desktopMapZoom}
              onZoomLevelChange={setDesktopMapZoom}
            />
          </div>

          <div className="absolute right-5 bottom-5 w-[380px] max-w-[calc(100%-40px)] bg-white/95 backdrop-blur rounded-2xl border border-gray-200 shadow-xl p-4" style={{width: '100%'}}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-gray-900">선택된 업체</span>
              <span
                className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${
                  canContinue ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {selectedShops.length} / {tourSize}
              </span>
            </div>

            {selectedShops.length > 0 ? (
              <div className="flex flex-wrap gap-2 mb-3">
                {selectedShops.map((shop) => {
                  const isFixed = fixedVendorIds.has(shop.id);
                  return (
                    <div
                      key={shop.id}
                      className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                        isFixed
                          ? 'bg-amber-100 text-amber-800 border-2 border-amber-400'
                          : 'bg-blue-50 text-blue-700'
                      }`}
                    >
                      <button
                        onClick={() => toggleFixed(shop.id)}
                        className="whitespace-nowrap hover:opacity-80 transition-opacity"
                      >
                        {shop.shopName}
                        {isFixed && <i className="ri-pushpin-fill ml-1 text-xs"></i>}
                      </button>
                      <button
                        onClick={() => removeVendor(shop.id)}
                        className="w-4 h-4 flex items-center justify-center hover:bg-blue-100 rounded-full transition-colors"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-gray-400 py-1 mb-3">
                왼쪽 목록에서 업체를 선택해주세요
              </p>
            )}

            <div className="mb-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-700 text-center leading-relaxed">
                {getStatusMessage()}
              </p>
            </div>

            <button
              onClick={handleContinue}
              disabled={!canContinue}
              className={`w-full py-4 font-semibold rounded-xl transition-colors whitespace-nowrap ${
                canContinue
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {canContinue
                ? '가능 일정 찾기'
                : `업체를 ${Math.max(0, 2 - selectedShops.length)}개 더 선택해주세요`}
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE CTA */}
      {selectedShops.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-5 py-4 shadow-lg md:hidden">
          <div className="flex flex-wrap gap-2 mb-3">
            {selectedShops.map((shop) => {
              const isFixed = fixedVendorIds.has(shop.id);
              return (
                <div
                  key={shop.id}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                    isFixed
                      ? 'bg-amber-100 text-amber-800 border-2 border-amber-400'
                      : 'bg-blue-50 text-blue-700'
                  }`}
                >
                  <button
                    onClick={() => toggleFixed(shop.id)}
                    className="whitespace-nowrap hover:opacity-80 transition-opacity"
                  >
                    {shop.shopName}
                    {isFixed && <i className="ri-pushpin-fill ml-1 text-xs"></i>}
                  </button>
                  <button
                    onClick={() => removeVendor(shop.id)}
                    className="w-4 h-4 flex items-center justify-center hover:bg-blue-100 rounded-full transition-colors"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mb-3 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-700 text-center leading-relaxed">
              {getStatusMessage()}
            </p>
          </div>

          <button
            onClick={() => setMobileMapOpen((prev) => !prev)}
            className="w-full mb-2 py-2.5 flex items-center justify-center gap-2 bg-gray-100 text-gray-700 font-medium rounded-xl text-sm hover:bg-gray-200 transition-colors whitespace-nowrap"
          >
            <i className="ri-map-pin-line text-base"></i>
            {mobileMapOpen ? '지도 닫기' : '지도에서 위치 보기'}
            {mobileMapOpen ? (
              <i className="ri-arrow-up-s-line text-base"></i>
            ) : (
              <i className="ri-arrow-down-s-line text-base"></i>
            )}
          </button>

          {mobileMapOpen && (
            <div className="mb-2 rounded-xl overflow-hidden border border-gray-200 bg-white">
              <VendorSelectionMapPanel
                clientId={NAVER_MAP_CLIENT_ID}
                selectedShops={selectedShopsWithCoords}
                fixedVendorIds={fixedVendorIds}
                selectedMarkerId={selectedMarkerId}
                onSelectMarker={setSelectedMarkerId}
                zoomLevel={desktopMapZoom}
                onZoomLevelChange={setDesktopMapZoom}
                className="h-64"
              />
            </div>
          )}

          <button
            onClick={handleContinue}
            className="w-full py-4 bg-blue-600 text-white font-semibold rounded-xl shadow-md hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            가능 일정 찾기
          </button>
        </div>
      )}
    </div>
  );
}